import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, RefreshCw, Send, CheckCircle2, XCircle } from "lucide-react";

const authHeaders = () => {
    const token = localStorage.getItem("session_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminMarketing() {
    const [data, setData] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [resending, setResending] = useState({});
    const [sweepingReengagement, setSweepingReengagement] = useState(false);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await axios.get(`${API}/admin/marketing/contacts`, {
                headers: authHeaders(),
            });
            setData(res.data);
        } catch (err) {
            toast.error("Couldn't load subscribers");
            setData({ contacts: [], stats: {} });
        } finally {
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const resendWelcome = async (email) => {
        setResending((r) => ({ ...r, [email]: true }));
        try {
            const res = await axios.post(
                `${API}/admin/marketing/contacts/${encodeURIComponent(email)}/resend-welcome`,
                {},
                { headers: authHeaders() },
            );
            if (res.data?.ok) {
                toast.success(`Welcome email re-sent to ${email}`);
            } else {
                toast.error("Resend failed — check Resend dashboard");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed");
        } finally {
            setResending((r) => ({ ...r, [email]: false }));
        }
    };

    const fireReengagement = async () => {
        if (!window.confirm(
            "Send re-engagement emails to all subscribers who joined 7+ days ago and haven't ordered yet?\n\nEach person gets a fresh WELCOME10 code (14-day expiry). Skips anyone already re-engaged in the last 14 days.",
        )) return;
        setSweepingReengagement(true);
        try {
            const res = await axios.post(
                `${API}/admin/marketing/reengagement/sweep`,
                {},
                { headers: authHeaders() },
            );
            const fired = res.data?.fired || 0;
            const skipped = res.data?.skipped || 0;
            toast.success(
                fired === 0
                    ? `No one needed a nudge right now (skipped ${skipped})`
                    : `Re-engagement sweep fired · ${fired} sent · ${skipped} skipped`,
            );
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sweep failed");
        } finally {
            setSweepingReengagement(false);
        }
    };

    if (!data) {
        return (
            <div className="flex items-center gap-2 text-muted2">
                <Loader2 className="animate-spin" size={16} /> Loading subscribers…
            </div>
        );
    }

    const { contacts, stats } = data;

    return (
        <div data-testid="admin-marketing-panel">
            <div className="bg-cream/40 border border-mint rounded-2xl p-4 mb-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="font-bold text-navy text-sm">
                        Re-engagement sweep
                    </p>
                    <p className="mt-0.5 text-xs text-muted2">
                        Nudges subscribers who joined 7+ days ago and never
                        ordered. Auto-runs every 6h — or fire it on demand.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="admin-marketing-fire-reengagement"
                    onClick={fireReengagement}
                    disabled={sweepingReengagement}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-forest hover:bg-forestDark text-white text-xs font-bold transition-all active:scale-95 disabled:opacity-50 flex-shrink-0"
                >
                    {sweepingReengagement ? (
                        <Loader2 size={12} className="animate-spin" />
                    ) : (
                        <Send size={12} />
                    )}
                    Send re-engagement now
                </button>
            </div>

            <div className="flex items-center justify-between mb-5">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                    {contacts.length} subscriber{contacts.length === 1 ? "" : "s"}
                </p>
                <button
                    data-testid="admin-marketing-refresh"
                    onClick={load}
                    disabled={refreshing}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-forest hover:text-forestDark disabled:opacity-50"
                >
                    <RefreshCw
                        size={14}
                        className={refreshing ? "animate-spin" : ""}
                    />
                    Refresh
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
                <Stat label="Total" value={stats.total ?? 0} accent="text-navy" />
                <Stat label="Active" value={stats.active ?? 0} accent="text-forest" />
                <Stat
                    label="Welcome Redeemed"
                    value={stats.welcome_redeemed ?? 0}
                    accent="text-gold"
                />
                <Stat
                    label="Unsubscribed"
                    value={stats.unsubscribed ?? 0}
                    accent="text-muted2"
                />
            </div>

            {/* Subscribers list */}
            {contacts.length === 0 ? (
                <div className="bg-white rounded-[20px] p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                    <Mail size={36} className="mx-auto text-mint" />
                    <p className="mt-3 font-bold text-navy">
                        No subscribers yet
                    </p>
                    <p className="mt-1 text-sm text-muted2">
                        When someone joins via the popup, they'll appear here.
                    </p>
                </div>
            ) : (
                <ul className="space-y-2.5">
                    {contacts.map((c) => (
                        <li
                            key={c.email}
                            data-testid={`subscriber-${c.email}`}
                            className="bg-white rounded-2xl p-4 shadow-[0_4px_18px_rgba(0,0,0,0.06)] flex items-start justify-between gap-3 flex-wrap"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-navy break-all">
                                        {c.email}
                                    </span>
                                    {c.unsubscribed ? (
                                        <Pill color="bg-burnt/15 text-burnt">
                                            <XCircle size={11} /> Unsubscribed
                                        </Pill>
                                    ) : (
                                        <Pill color="bg-mint text-forest">
                                            <CheckCircle2 size={11} /> Active
                                        </Pill>
                                    )}
                                    {c.welcome_redeemed && (
                                        <Pill color="bg-gold/20 text-burnt">
                                            🎟️ WELCOME10 used
                                        </Pill>
                                    )}
                                    {c.repeat_code && !c.repeat_redeemed && (
                                        <Pill color="bg-forest/10 text-forest">
                                            REPEAT10 active
                                        </Pill>
                                    )}
                                </div>
                                <p className="mt-1 text-xs text-muted2">
                                    Joined {formatDate(c.subscribed_at)} ·
                                    Source: <span className="font-bold">{c.source || "—"}</span>
                                </p>
                                {c.welcome_code && (
                                    <p className="mt-0.5 text-xs text-muted2">
                                        Code <span className="font-mono font-bold text-forest">{c.welcome_code}</span> · expires {c.welcome_expires_at?.slice(0, 10) || "—"}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    data-testid={`resend-welcome-${c.email}`}
                                    onClick={() => resendWelcome(c.email)}
                                    disabled={
                                        c.unsubscribed || resending[c.email]
                                    }
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-forest text-white text-xs font-bold hover:bg-forestDark transition-all active:scale-95 disabled:opacity-50"
                                >
                                    {resending[c.email] ? (
                                        <Loader2 size={12} className="animate-spin" />
                                    ) : (
                                        <Send size={12} />
                                    )}
                                    Resend welcome
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className="bg-white rounded-[16px] p-4 shadow-[0_4px_16px_rgba(0,0,0,0.05)]">
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted2">
                {label}
            </p>
            <p className={`mt-1 font-serif font-black text-2xl ${accent}`}>
                {value}
            </p>
        </div>
    );
}

function Pill({ children, color }) {
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${color}`}
        >
            {children}
        </span>
    );
}
